import { LEGACY_PRODUCTION_HOST, PRODUCTION_HOST } from './constants';

function isProductionHost(host: string): boolean {
  return (
    host === PRODUCTION_HOST ||
    host === `www.${PRODUCTION_HOST}` ||
    host === LEGACY_PRODUCTION_HOST
  );
}

function escapeHost(host: string): string {
  return host.replaceAll('.', String.raw`\.`);
}

function normalizeDomainHost(value: string | undefined): string {
  let host = (value ?? '').trim().toLowerCase();

  if (host.startsWith('https://')) {
    host = host.slice('https://'.length);
  } else if (host.startsWith('http://')) {
    host = host.slice('http://'.length);
  }

  while (host.endsWith('/')) {
    host = host.slice(0, -1);
  }

  return host;
}

function resolveTargetDomain(): string {
  const environmentDomain = normalizeDomainHost(
    process.env.REACT_APP_DOMAIN_NAME
  );
  return environmentDomain || normalizeDomainHost(globalThis.location?.host);
}

const environmentDomain = resolveTargetDomain();

/**
 * Computed once when the module loads. Production avoids all URL matching
 * and replacement work after this single check.
 */
export const isProductionDomainSwapEnabled =
  !isProductionHost(environmentDomain);

const productionOriginPattern = new RegExp(
  `(?:https?:)?\\/\\/(?:(?:www\\.)?${escapeHost(PRODUCTION_HOST)}|${escapeHost(LEGACY_PRODUCTION_HOST)})(?=[#/:?]|$)`,
  'gi'
);

export function getEnvironmentDomain(): string {
  return environmentDomain;
}

/**
 * Replaces only the production Aosphere origins (`aoslogin.com`, `www.` and
 * `ng.`). Paths, query strings (including encrypted eurl values), and hashes
 * remain byte-for-byte intact.
 */
export function swapProductionDomain<T extends string | null | undefined>(
  url: T
): T {
  if (!isProductionDomainSwapEnabled || !url) {
    return url;
  }

  return url.replaceAll(
    productionOriginPattern,
    `https://${environmentDomain}`
  ) as T;
}

function transformPayloadValue(value: unknown, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    return swapProductionDomain(value);
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (seen.has(value)) {
    return value;
  }

  seen.add(value);

  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      value[index] = transformPayloadValue(value[index], seen);
    }
    return value;
  }

  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    record[key] = transformPayloadValue(record[key], seen);
  }

  return record;
}

/** Deep-walks a successful GET payload and rewrites production-domain URLs. */
export function transformApiPayloadDomains<T>(payload: T): T {
  if (!isProductionDomainSwapEnabled) {
    return payload;
  }

  return transformPayloadValue(payload, new WeakSet()) as T;
}
